import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmailTracking, clearEmailTracking } from '../../../../redux/globalData/emailTrackingSlice';
import { INVOICE_STATUS } from '../../../../utils/constants';
import {
  TIMELINE_STEP_ORDER,
  EMAIL_STEPS_REACHED,
  STEP_LABEL,
  STEP_DESC_DONE,
  STEP_DESC_PENDING,
  EMAIL_BOUNCED_DESC,
  EMAIL_FAILED_DESC,
  mapTimelineStatusLabel,
} from '../../../../utils/emailTracking';
import { TimelineStatusIcon, StepIcon, CloseIcon, ChevronDownIcon, ChevronUpIcon } from './EmailTrackingIcons';
import './EmailTrackingModal.css';

/** invoiceSalaryStatus value that means salary has been paid out. */
const SALARY_PAID_STATUS = 2;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

// 24-hour clock — Europe doesn't use am/pm. `en-GB` renders midnight as
// "00:00" (an `en-US`/hour12:false combination yields "24:00" instead).
const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';

const formatDateTime = (iso) => (iso ? `${formatDate(iso)} ${formatTime(iso)}` : '—');

/**
 * Six-step delivery timeline for one invoice: Sent / Delivered / Opened (from
 * the SendGrid tracking endpoint) followed by Paid / Salary Created / Salary
 * Paid (derived from the invoice row's own status flags).
 */
const EmailTrackingModal = ({ show, invoiceId, invoiceStatus, isSalaryCreated, invoiceSalaryStatus, onClose }) => {
  const dispatch = useDispatch();
  const { details, loading, error } = useSelector((state) => state.emailTracking);
  const modalRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [showScrollUpHint, setShowScrollUpHint] = useState(false);

  // Load on open; clear on close so a stale timeline never flashes next time.
  useEffect(() => {
    if (show && invoiceId !== null && invoiceId !== undefined) {
      dispatch(fetchEmailTracking(invoiceId));
    }
    if (!show) {
      dispatch(clearEmailTracking());
    }
  }, [show, invoiceId, dispatch]);

  // Close on Escape.
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onClose]);

  // Show "scroll down"/"scroll up" hints whenever the modal is taller than its
  // viewport and there's unscrolled content in that direction — re-checked on
  // scroll, whenever content finishes loading (its height changes), and on
  // window resize.
  const checkScrollable = useCallback(() => {
    const el = modalRef.current;
    if (!el) return;
    setShowScrollHint(el.scrollHeight - el.clientHeight - el.scrollTop > 24);
    setShowScrollUpHint(el.scrollTop > 24);
  }, []);

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, [show, loading, details, checkScrollable]);

  const scrollToBottom = () => {
    modalRef.current?.scrollTo({ top: modalRef.current.scrollHeight, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!show) return null;

  const status = details?.status || 'not_sent';
  const events = details?.events || [];

  // --- Invoice-progress derivation ---
  // Each node reflects only its own actual flag/event — express payment can
  // create (and pay) a salary without the invoice ever being marked paid or
  // the email ever being opened, so no stage is forced complete by a later one.
  const salaryPaidDone = invoiceSalaryStatus === SALARY_PAID_STATUS;
  const salaryCreatedDone = !!isSalaryCreated;
  const invoicePaidDone = invoiceStatus === INVOICE_STATUS.PAID;

  const emailReached = EMAIL_STEPS_REACHED[status] || 0;

  let failIndex = -1;
  if (status === 'failed') failIndex = 0;
  else if (status === 'bounced') failIndex = 1;
  const isBounce = failIndex >= 0 && status === 'bounced';

  // TIMELINE_STEP_ORDER: [sent, delivered, opened, paid, salaryCreated, salaryPaid]
  const doneAt = (i) => {
    if (i < 3) return i < emailReached;
    if (i === 3) return invoicePaidDone;
    if (i === 4) return salaryCreatedDone;
    return salaryPaidDone;
  };

  let lastDoneIndex = -1;
  TIMELINE_STEP_ORDER.forEach((_, i) => {
    if (doneAt(i)) lastDoneIndex = i;
  });

  // Header pill / status text = furthest stage reached.
  let timelineStatus = status;
  if (salaryPaidDone) timelineStatus = 'salary_paid';
  else if (salaryCreatedDone) timelineStatus = 'salary_created';
  else if (invoicePaidDone) timelineStatus = 'paid';

  // Portal to document.body so the overlay isn't trapped by the invoice
  // table's own stacking/transform context.
  return ReactDOM.createPortal(
    <div className="email-tracking-modal-overlay" role="presentation" onClick={onClose}>
      {/* Non-scrolling shell — lets the scroll hints float over the modal's own
          scrolling content instead of scrolling away with it. */}
      <div className="email-tracking-modal-shell" role="presentation" onClick={(e) => e.stopPropagation()}>
        <div
          className="email-tracking-modal"
          ref={modalRef}
          onScroll={checkScrollable}
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-tracking-modal-title"
        >
          {/* Header */}
          <div className="email-tracking-modal__header">
            <div className="email-tracking-modal__header-row1">
              <h2 className="email-tracking-modal__title" id="email-tracking-modal-title">
                Invoice Tracking
              </h2>
              <button type="button" className="email-tracking-modal__close" onClick={onClose} aria-label="Close">
                <CloseIcon />
              </button>
            </div>

            {!loading && !error && details && (
              <div className="email-tracking-modal__header-row2">
                <div className="email-tracking-modal__status-block">
                  <div className="email-tracking-modal__status-icon">
                    <span className="email-tracking-modal__status-icon-glyph">
                      <TimelineStatusIcon status={timelineStatus} size={30} />
                    </span>
                  </div>
                  <div className="email-tracking-modal__status-text">
                    <span className="email-tracking-modal__status-label">Current status</span>
                    <span className="email-tracking-modal__status-value">
                      {mapTimelineStatusLabel(timelineStatus)}
                    </span>
                    {details.invoiceNumber && (
                      <span className="email-tracking-modal__status-invoice">Invoice #{details.invoiceNumber}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="email-tracking-modal__body">
            {loading && (
              <div className="email-tracking-modal__state">
                <span className="email-tracking-modal__spinner" aria-hidden="true" />
                <span>Loading</span>
              </div>
            )}

            {!loading && error && (
              <div className="email-tracking-modal__state email-tracking-modal__state--error">
                Couldn&apos;t load email tracking. Please try again.
              </div>
            )}

            {!loading && !error && details && (
              <div className="email-tracking-modal__timeline">
                {TIMELINE_STEP_ORDER.map((type, i) => {
                  const ev = i < 3 ? events[i] : undefined;
                  const ts = ev?.timestamp || null;
                  const done = doneAt(i);
                  const isFail = i === failIndex;
                  const isLast = i === TIMELINE_STEP_ORDER.length - 1;

                  // one of: done | active | pending | fail
                  let state = 'pending';
                  if (isFail) state = 'fail';
                  else if (done) state = failIndex < 0 && i === lastDoneIndex ? 'active' : 'done';

                  const hasTransit = state === 'active' && !isLast;

                  let time = '—';
                  if (isFail) time = formatDateTime(details.sentOn);
                  else if (ts) time = formatDateTime(ts);

                  let desc = done ? STEP_DESC_DONE[type] : STEP_DESC_PENDING[type];
                  if (isFail) desc = isBounce ? EMAIL_BOUNCED_DESC : EMAIL_FAILED_DESC;

                  const rowCls = [
                    'email-tracking-modal__row',
                    `email-tracking-modal__row--${state}`,
                    isFail && isBounce ? 'email-tracking-modal__row--bounce' : '',
                    hasTransit ? 'email-tracking-modal__row--transit' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div className={rowCls} key={type} style={{ '--i': i }}>
                      <div className="email-tracking-modal__row-icon">
                        <StepIcon type={type} size={state === 'pending' ? 16 : 20} />
                      </div>
                      <div className="email-tracking-modal__row-body">
                        <div className="email-tracking-modal__row-top">
                          <span className="email-tracking-modal__row-title">{STEP_LABEL[type]}</span>
                          <span className="email-tracking-modal__row-time">{time}</span>
                        </div>
                        <div className="email-tracking-modal__row-desc">{desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="email-tracking-modal__footer">
            <button type="button" className="email-tracking-modal__btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {showScrollUpHint && (
          <button
            type="button"
            className="email-tracking-modal__scroll-hint email-tracking-modal__scroll-hint--up"
            onClick={scrollToTop}
            aria-label="Scroll up"
          >
            <ChevronUpIcon size={16} />
          </button>
        )}
        {showScrollHint && (
          <button
            type="button"
            className="email-tracking-modal__scroll-hint email-tracking-modal__scroll-hint--down"
            onClick={scrollToBottom}
            aria-label="Scroll down"
          >
            <ChevronDownIcon size={16} />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

EmailTrackingModal.propTypes = {
  show: PropTypes.bool,
  /** Internal numeric invoice id whose tracking to load; null when closed. */
  invoiceId: PropTypes.number,
  /** Invoice-progress flags carried off the invoice row (drive the last three nodes). */
  invoiceStatus: PropTypes.number,
  isSalaryCreated: PropTypes.bool,
  invoiceSalaryStatus: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default EmailTrackingModal;
